var _excluded = ["icon"],
  _excluded2 = ["customInput", "popperClassName", "calendarClassName", "enableTabLoop", "dateFormat", "placeholderText", "renderCustomHeader", "popperPlacement", "dayClassName", "prevMonthActionIcon", "nextMonthActionIcon", "dropdownIcon", "yearDropDownFromTo", "yearDropDownSortOrder", "selectYearAriaLabel", "selectMonthAriaLabel", "language", "months", "className", "sx"],
  _excluded3 = ["customInput", "calendarClassName", "enableTabLoop", "dateFormat", "placeholderText", "renderCustomHeader", "dayClassName", "prevMonthActionIcon", "nextMonthActionIcon", "dropdownIcon", "yearDropDownFromTo", "yearDropDownSortOrder", "selectYearAriaLabel", "selectMonthAriaLabel", "modalAriaLabel", "language", "months", "className", "sx", "selected", "onChange", "modalProps"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { useState, useMemo } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getYear, getMonth, format } from 'date-fns';
import cy from 'date-fns/locale/cy';
import enGb from 'date-fns/locale/en-GB';
import { forwardRef, tqc, useMultiStyleConfig } from '../../system';
import { cx, pick, split } from '../../utils';
import { FaCalendarAlt, FaAngleRight, FaAngleLeft } from 'react-icons/fa';
registerLocale('cy-GB', cy);
registerLocale('en-GB', enGb);
import { Input, InputGroup, InputRightElement } from '../input';
import { Button, IconButton } from '../button';
import { Select } from '../select';
import { Flex } from '../layout';
import { IconDoubleChevronLeft, IconDoubleChevronRight } from '../icons';
import { numberRange } from '../../utils';
import { layoutPropNames } from '../../styled-system';
import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay } from '../modal';
import { useThemedValueSwitch } from '../../hooks';
var supportedLanguages = {
  'en-GB': enGb,
  'cy-GB': cy
};
/**
 * Returns a list of months in correct format based on supplied
 * language code
 *
 * @param language What language should months be returned as.
 * @param months A list of months to use instead of a language.
 * @returns string[]
 */
var useMonth = (language, months) => {
  var createMonthsList = language => {
    return Array.from({
      length: 12
    }, (_, i) => i + 1).map(monthNumber => format(new Date(2000, monthNumber, 0, 0, 0, 0), 'LLL', {
      locale: supportedLanguages[language]
    }));
  };
  return useMemo(() => {
    if (months) return months;
    if (language) {
      return createMonthsList(language);
    } else {
      return createMonthsList('en-GB');
    }
  }, [months, language]);
};
export var DatePickerInput = forwardRef((props, ref) => {
  var [layoutProps, iProps] = split(props, [...layoutPropNames, 'sx']);
  var _ref = iProps,
    {
      icon
    } = _ref,
    inputProps = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(InputGroup, layoutProps, /*#__PURE__*/React.createElement(Input, _extends({}, inputProps, {
    ref: ref
  })), /*#__PURE__*/React.createElement(InputRightElement, {
    sx: {
      pointerEvents: 'none'
    },
    className: "tqc-date-picker__calendar-icon",
    children: icon !== null && icon !== void 0 ? icon : /*#__PURE__*/React.createElement(FaCalendarAlt, null)
  }));
});
export var DatePickerHeader = _ref2 => {
  var {
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    yearDropDownFromTo = [1950, 2030],
    yearDropDownSortOrder = 'descending',
    months: monthsProp,
    selectMonthAriaLabel,
    selectYearAriaLabel,
    prevMonthIcon,
    nextMonthIcon,
    dropdownIcon
  } = _ref2;
  var [yearFrom, yearTo] = yearDropDownFromTo;
  var yearsRange = React.useMemo(() => {
    var years = numberRange(yearFrom, yearTo);
    if (yearDropDownSortOrder === 'descending') {
      years.reverse();
    }
    return years;
  }, [yearFrom, yearTo, yearDropDownSortOrder]);
  return /*#__PURE__*/React.createElement(Flex, {
    className: "tqc-date-picker__header",
    "data-testid": "datepicker-header"
  }, /*#__PURE__*/React.createElement(IconButton, {
    className: "tqc-date-picker__nav-button tqc-date-picker__nav-button--prev",
    "aria-label": "Previous month",
    icon: prevMonthIcon,
    onClick: decreaseMonth,
    disabled: prevMonthButtonDisabled,
    isRound: false,
    type: "button"
  }), /*#__PURE__*/React.createElement(Select, {
    "data-testid": "datepicker-month-select",
    className: "tqc-date-picker__month-select",
    mr: "1",
    width: "100%",
    "aria-label": selectMonthAriaLabel,
    icon: dropdownIcon,
    menuPortalTarget: null,
    value: monthsProp[getMonth(date)],
    onChange: value => changeMonth(monthsProp.indexOf(value))
  }, monthsProp.map(month => /*#__PURE__*/React.createElement("option", {
    key: month,
    value: month
  }, month))), /*#__PURE__*/React.createElement(Select, {
    "data-testid": "datepicker-year-select",
    className: "tqc-date-picker__year-select",
    width: "100%",
    "aria-label": selectYearAriaLabel,
    icon: dropdownIcon,
    menuPortalTarget: null,
    value: getYear(date),
    onChange: value => changeYear(Number(value))
  }, yearsRange.map(year => /*#__PURE__*/React.createElement("option", {
    key: year,
    value: year
  }, year))), /*#__PURE__*/React.createElement(IconButton, {
    className: "tqc-date-picker__nav-button tqc-date-picker__nav-button--next",
    "aria-label": "Next month",
    icon: nextMonthIcon,
    onClick: increaseMonth,
    disabled: nextMonthButtonDisabled,
    isRound: false,
    type: "button"
  }));
};
var TQCDatePicker = tqc(ReactDatePicker);
export var DatePicker = forwardRef((props, ref) => {
  var inputProps = pick(props, ['isInvalid', 'isDisabled', 'isReadOnly', 'isRequired', 'icon']);
  var {
      customInput = /*#__PURE__*/React.createElement(DatePickerInput, _extends({}, inputProps, {
        "data-testid": props['data-testid'] ? props['data-testid'] : undefined
      })),
      popperClassName,
      calendarClassName,
      enableTabLoop = true,
      dateFormat = 'dd/MM/yyyy',
      placeholderText = 'dd/mm/yyyy',
      renderCustomHeader: CustomHeader = DatePickerHeader,
      popperPlacement = 'top',
      dayClassName: _dayClassName = () => '',
      prevMonthActionIcon,
      nextMonthActionIcon,
      dropdownIcon,
      yearDropDownFromTo,
      yearDropDownSortOrder,
      selectYearAriaLabel,
      selectMonthAriaLabel,
      language,
      months,
      className,
      sx
    } = props,
    rest = _objectWithoutProperties(props, _excluded2);
  var {
    isWales,
    isTQ
  } = useThemedValueSwitch();
  var showSingleFaAngleIcon = isWales || isTQ;
  var defaultNextMonthIcon = showSingleFaAngleIcon ? /*#__PURE__*/React.createElement(FaAngleRight, {
    "data-testid": "datepicker-next"
  }) : /*#__PURE__*/React.createElement(IconDoubleChevronRight, {
    "data-testid": "datepicker-next"
  });
  var defaultPrevMonthIcon = showSingleFaAngleIcon ? /*#__PURE__*/React.createElement(FaAngleLeft, {
    "data-testid": "datepicker-previous"
  }) : /*#__PURE__*/React.createElement(IconDoubleChevronLeft, {
    "data-testid": "datepicker-previous"
  });
  var monthsList = useMonth(language, months);
  var styles = useMultiStyleConfig('DatePicker', _objectSpread({}, props));
  return /*#__PURE__*/React.createElement(tqc.div, {
    className: cx('tqc-date-picker', className),
    __css: styles.root,
    sx: sx
  }, /*#__PURE__*/React.createElement(TQCDatePicker, _extends({
    ref: ref,
    locale: language !== null && language !== void 0 ? language : 'en-GB',
    enableTabLoop: enableTabLoop,
    dateFormat: dateFormat,
    placeholderText: placeholderText,
    popperClassName: cx('tqc-date-picker__popper', popperClassName),
    calendarClassName: cx('tqc-date-picker__calendar', calendarClassName),
    dayClassName: date => cx(_dayClassName(date), 'tqc-date-picker__day'),
    popperPlacement: popperPlacement,
    customInput: customInput,
    preventOpenOnFocus: true,
    useWeekdaysShort: true,
    renderCustomHeader: props => /*#__PURE__*/React.createElement(CustomHeader, _extends({}, props, {
      months: monthsList,
      dropdownIcon: dropdownIcon,
      nextMonthIcon: nextMonthActionIcon !== null && nextMonthActionIcon !== void 0 ? nextMonthActionIcon : defaultNextMonthIcon,
      prevMonthIcon: prevMonthActionIcon !== null && prevMonthActionIcon !== void 0 ? prevMonthActionIcon : defaultPrevMonthIcon,
      yearDropDownFromTo: yearDropDownFromTo,
      selectYearArialabel: selectYearAriaLabel,
      selectMonthAriaLabel: selectMonthAriaLabel,
      yearDropDownSortOrder: yearDropDownSortOrder
    }))
  }, rest)));
});
export var MobileDatePicker = forwardRef((props, ref) => {
  var [isOpen, setIsOpen] = useState(false);
  var inputProps = pick(props, ['isInvalid', 'isDisabled', 'isReadOnly', 'isRequired', 'icon']);
  var {
      customInput,
      calendarClassName,
      enableTabLoop = true,
      dateFormat = 'dd/MM/yyyy',
      placeholderText = 'dd/mm/yyyy',
      renderCustomHeader: CustomHeader = DatePickerHeader,
      dayClassName: _dayClassName2 = () => '',
      prevMonthActionIcon,
      nextMonthActionIcon,
      dropdownIcon,
      yearDropDownFromTo,
      yearDropDownSortOrder,
      selectYearAriaLabel,
      selectMonthAriaLabel,
      modalAriaLabel,
      language,
      months,
      className,
      sx,
      selected = null,
      onChange,
      modalProps
    } = props,
    rest = _objectWithoutProperties(props, _excluded3);
  var {
    isWales,
    isTQ
  } = useThemedValueSwitch();
  var showSingleFaAngleIcon = isWales || isTQ;
  var defaultNextMonthIcon = showSingleFaAngleIcon ? /*#__PURE__*/React.createElement(FaAngleRight, {
    "data-testid": "datepicker-next"
  }) : /*#__PURE__*/React.createElement(IconDoubleChevronRight, {
    "data-testid": "datepicker-next"
  });
  var defaultPrevMonthIcon = showSingleFaAngleIcon ? /*#__PURE__*/React.createElement(FaAngleLeft, {
    "data-testid": "datepicker-previous"
  }) : /*#__PURE__*/React.createElement(IconDoubleChevronLeft, {
    "data-testid": "datepicker-previous"
  });
  var monthsList = useMonth(language, months);
  var styles = useMultiStyleConfig('DatePicker', props);
  var [value, setValue] = useState(selected);
  var [tempValue, setTempValue] = useState(selected);
  var open = () => {
    setTempValue(selected);
    setIsOpen(true);
  };
  var cancel = () => {
    setTempValue(selected);
    setIsOpen(false);
  };
  var apply = () => {
    setValue(tempValue);
    onChange(tempValue, undefined);
    setIsOpen(false);
  };
  return /*#__PURE__*/React.createElement(tqc.div, {
    className: cx('tqc-date-picker', className),
    __css: styles.root,
    sx: sx
  }, customInput || /*#__PURE__*/React.createElement(DatePickerInput, _extends({}, inputProps, {
    "data-testid": props['data-testid'] ? props['data-testid'] : undefined,
    value: value ? format(value, dateFormat) : '',
    placeholder: placeholderText,
    onClick: open,
    onChange: e => {
      e.preventDefault();
      open();
    }
  })), /*#__PURE__*/React.createElement(Modal, {
    isOpen: isOpen,
    onClose: cancel
  }, /*#__PURE__*/React.createElement(ModalOverlay, null), /*#__PURE__*/React.createElement(ModalContent, {
    "aria-label": modalAriaLabel,
    containerProps: {
      zIndex: '1600'
    },
    sx: styles.modalContainer
  }, /*#__PURE__*/React.createElement(ModalHeader, null, /*#__PURE__*/React.createElement("h1", null, modalProps.title), /*#__PURE__*/React.createElement(ModalCloseButton, {
    sx: {
      h: '2rem',
      fontSize: '1rem'
    }
  })), /*#__PURE__*/React.createElement(ModalBody, {
    sx: styles.modalBody
  }, /*#__PURE__*/React.createElement(tqc.div, {
    className: cx('tqc-date-picker', className),
    __css: styles.root
  }, /*#__PURE__*/React.createElement(TQCDatePicker, _extends({
    ref: ref,
    enableTabLoop: enableTabLoop,
    dateFormat: dateFormat,
    calendarClassName: cx('tqc-date-picker__calendar tqc-date-picker__calendar--mobile', calendarClassName),
    dayClassName: date => cx(_dayClassName2(date), 'tqc-date-picker__day tqc-date-picker__day--mobile'),
    inline: true,
    selected: tempValue,
    locale: language !== null && language !== void 0 ? language : 'en-GB',
    useWeekdaysShort: true,
    renderCustomHeader: props => /*#__PURE__*/React.createElement(CustomHeader, _extends({}, props, {
      months: monthsList,
      dropdownIcon: dropdownIcon,
      nextMonthIcon: nextMonthActionIcon !== null && nextMonthActionIcon !== void 0 ? nextMonthActionIcon : defaultNextMonthIcon,
      prevMonthIcon: prevMonthActionIcon !== null && prevMonthActionIcon !== void 0 ? prevMonthActionIcon : defaultPrevMonthIcon,
      yearDropDownFromTo: yearDropDownFromTo,
      selectYearArialabel: selectYearAriaLabel,
      selectMonthAriaLabel: selectMonthAriaLabel,
      yearDropDownSortOrder: yearDropDownSortOrder
    }))
  }, rest, {
    onChange: val => {
      setTempValue(val);
    }
  })))), /*#__PURE__*/React.createElement(ModalFooter, {
    sx: styles.modalFooter
  }, /*#__PURE__*/React.createElement(Button, {
    onClick: apply
  }, modalProps.applyText), /*#__PURE__*/React.createElement(Button, {
    variant: isTQ ? 'tertiary' : 'secondary',
    onClick: cancel
  }, modalProps.cancelText)))));
});